import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ClipboardList, Footprints, MessageSquareHeart, GraduationCap, BookOpenText, BookMarked, PlayCircle, ShoppingBag, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { PublicHeader, ExploreDemo } from "../components/PublicHeader";
import { uiLang } from "../components/ProductCard";
import { HeroVideo } from "../components/HeroVideo";
import { PublicFooter } from "../components/PublicFooter";
import { AppBadges } from "../components/AppBadges";

const POINTS = [["record", ClipboardList], ["visit", Footprints], ["owner", MessageSquareHeart]];
const LEGACY = { catalogue: "nav-catalogue", help: "nav-help", demo: "nav-live-demo", sign_in: "nav-sign-in", toggle: "landing-menu-toggle", nav: "landing-nav", close: "landing-menu-close" };
const excerpt = (b = "") => { const s = b.replace(/[#*[\]]/g, "").replace(/\s+/g, " ").trim(); return s.length > 220 ? `${s.slice(0, 220)}…` : s; };

function usePage(slug, lang) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/public/pages/${slug}`, { params: { lang } }).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug, lang]);
  return p;
}

function More({ to, testid, label, className = "mt-3" }) {
  return <Link to={to} data-testid={testid} className={`link-more ${className}`}>{label}<ArrowRight className="h-4 w-4" aria-hidden /></Link>;
}

export function useReveal() {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let fired = false, io;
    const show = () => { el.classList.add("is-in"); io?.disconnect(); window.removeEventListener("scroll", onScroll); };
    const onScroll = () => { if (el.getBoundingClientRect().top < window.innerHeight * 0.85) show(); };
    try {
      io = new IntersectionObserver((es) => { fired = true; if (es.some((e) => e.isIntersecting)) show(); }, { threshold: 0.2 });
      el.classList.add("rv-armed");
      io.observe(el);
      window.addEventListener("scroll", onScroll, { passive: true });
    } catch { show(); return undefined; }
    const t = setTimeout(() => { if (!fired) show(); }, 1500);
    return () => { clearTimeout(t); io?.disconnect(); window.removeEventListener("scroll", onScroll); };
  }, []);
  return ref;
}

function Sec({ n, icon: Icon, title, testid, children }) {
  const ref = useReveal();
  return (
    <section ref={ref} data-testid={testid} data-order={n} className="border-t border-sand-line py-12 sm:py-16">
      <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-3">
          <p className="eyebrow flex items-center gap-2"><span className="rv-num tabular-nums">{String(n).padStart(2, "0")}</span><span className="rv-line h-px w-8 bg-clay/40" aria-hidden /></p>
          <h2 className="rv-rise mt-3 flex items-start gap-3 font-display text-base font-semibold text-ink md:text-lg">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border bg-card shadow-xs"><Icon className="h-5 w-5 text-clay" aria-hidden /></span><span className="pt-2">{title}</span>
          </h2>
        </div>
        <div className="min-w-0 lg:col-span-9">{children}</div>
      </div>
    </section>
  );
}

function PageCard({ slug, p, testid, i = 0 }) {
  const { t } = useTranslation();
  if (!p) return null;
  return (
    <div className="rv-item surface flex h-full flex-col p-6" style={{ "--i": i }} data-testid={testid}>
      <h3 className="font-display text-lg font-semibold text-ink md:text-xl">{p.edition.title}</h3>
      {p.placeholder && <p className="mt-2 inline-flex w-fit rounded-md border border-amber-line bg-amber px-2 py-0.5 text-xs font-bold text-amber-ink" data-testid={`${testid}-placeholder`}>{t("b3.placeholder_page")}</p>}
      <p className="mt-3 text-[15px] text-muted-foreground">{excerpt(p.edition.sections?.lead || p.edition.sections?.intro || p.edition.body)}</p>
      <More to={`/about/${slug}`} testid={`${testid}-link`} label={t("b5.open_page", { title: p.edition.title })} className="mt-auto pt-4" />
    </div>
  );
}

function Cases({ lang }) {
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/public/cases", { params: { lang, limit: 3 } }).then((r) => setList(r.data)).catch(() => setList([])); }, [lang]);
  if (!list || !list.length) return null;
  return (
    <Sec n={3} icon={BookMarked} title={t("b5.sec_cases")} testid="landing-sec-cases">
      <ul className="grid gap-4 sm:grid-cols-3">{list.map((c, i) => (
        <li key={c.id} className="rv-item min-w-0" style={{ "--i": i }}><Link to={`/cases/${c.id}`} data-testid={`landing-case-${c.number}`} className="lift surface flex h-full flex-col p-5">
          <span className="w-fit rounded-md bg-muted px-2 py-0.5 text-xs font-bold text-neutral-ink" data-testid={`landing-case-kind-${c.number}`}>{t(`b3.kind_${c.kind}`)}</span>
          <h3 className="mt-3 font-display text-base font-semibold text-ink">{c.edition?.title || c.display_name}</h3>
          <ArrowRight className="mt-auto h-4 w-4 self-end text-clay" aria-hidden /></Link></li>))}</ul>
      <div className="rv-item" style={{ "--i": list.length }}><More to="/cases" testid="landing-cases-link" label={t("b5.all_cases")} /></div>
    </Sec>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const learning = usePage("learning", lang), method = usePage("methodology", lang), author = usePage("author", lang);
  return (
    <div className="min-h-screen grain" data-testid="landing-page">
      <PublicHeader ids={LEGACY} />
      <main className="mx-auto max-w-6xl px-4 pb-6 sm:px-6">
        <div className="grid items-center gap-8 pt-2 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-stretch lg:gap-10 lg:pt-10">
          <section className="fade-up" data-testid="hero-text-col">
            <p className="eyebrow">{t("landing.kicker")}</p>
            <h1 className="mt-3 font-display text-[2rem] font-semibold text-ink min-[400px]:text-4xl sm:text-5xl lg:mt-2 lg:text-[2.5rem] xl:text-[2.875rem]" data-testid="landing-title">{t("landing.title")}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-3 lg:mt-5">
              <ExploreDemo testid="explore-live-demo-btn" className="lg:px-5 lg:text-[15px]" />
              <Link to="/register" data-testid="landing-sign-up-btn" className="btn btn-lg btn-line lg:px-5 lg:text-[15px]">{t("auth.sign_up")}</Link>
            </div>
            <p className="mt-3 text-sm text-muted-foreground lg:mt-2">{t("landing.cta_note")}</p>
            <More to="/about/learning" testid="hero-courses-link" label={t("b6.courses_link")} className="mt-2 lg:mt-0" />
            <p className="mt-3 hidden max-w-xl text-base text-muted-foreground lg:mt-1 lg:block" data-testid="landing-sub">{t("landing.sub")}</p>
          </section>
          <div className="fade-up-2" data-testid="hero-media-col"><HeroVideo /><p className="mt-4 text-base text-muted-foreground lg:hidden" data-testid="landing-sub-mobile">{t("landing.sub")}</p></div>
        </div>
        <div className="mt-12">
          <Sec n={1} icon={GraduationCap} title={t("b5.sec_learning")} testid="landing-sec-learning"><PageCard slug="learning" p={learning} testid="landing-page-learning" /></Sec>
          <Sec n={2} icon={BookOpenText} title={t("b5.sec_method")} testid="landing-sec-method">
            <div className="grid gap-4 md:grid-cols-2"><PageCard slug="methodology" p={method} testid="landing-page-methodology" /><PageCard slug="author" p={author} testid="landing-page-author" i={1} /></div>
          </Sec>
          <Cases lang={lang} />
          <Sec n={4} icon={PlayCircle} title={t("b5.sec_demo")} testid="landing-sec-demo">
            <div className="rv-item surface grid gap-6 p-6">
              <div className="min-w-0"><ExploreDemo testid="landing-demo-section-btn" className="whitespace-nowrap max-sm:whitespace-normal" /><p className="mt-3 text-sm text-muted-foreground">{t("landing.cta_note")}</p></div>
              <div className="min-w-0 border-t pt-6" data-testid="landing-apps">
                <p className="font-display text-base font-semibold text-ink md:text-lg">{t("b7.apps")}</p>
                <p className="mb-4 mt-1 text-sm text-muted-foreground">{t("b7.apps_note")}</p>
                <AppBadges testid="landing-app-badges" />
              </div>
            </div>
          </Sec>
          <Sec n={5} icon={ShoppingBag} title={t("b5.sec_tools")} testid="landing-sec-tools">
            <ul className="grid gap-4 sm:grid-cols-3">{POINTS.map(([k, Icon], i) => (
              <li key={k} className="rv-item surface flex h-full flex-col gap-3 p-5 text-[15px]" style={{ "--i": i }}><span className="grid h-10 w-10 place-items-center rounded-xl bg-peach"><Icon className="h-5 w-5 text-clay" aria-hidden /></span><b className="font-semibold text-ink">{t(`landing.p_${k}`)}</b></li>))}</ul>
            <div className="rv-item mt-2 flex flex-wrap gap-x-6" style={{ "--i": POINTS.length }}><More to="/catalogue" testid="landing-catalogue-link" label={t("b5.open_catalogue")} /><More to="/register" testid="landing-tools-sign-up" label={t("auth.sign_up")} /></div>
          </Sec>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
